import { useCallback, useState } from "react";
import { Location, Permission } from "@forgepoint/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import { DeleteConfirmationDialog } from "@/components/DeleteConfirmationDialog";
import { Loader, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useEntitySearch } from "@/lib/useEntitySearch";
import { useLocationsStore } from "./locationsStore";
import { LocationEditorSheet } from "./LocationEditorSheet";
import { useHasPermission } from "@/store/authStore";

const PAGE_SIZE = 50;

export default function LocationsController() {
  const {
    locations,
    locationLoadingStates,
    listLoading,
    listError,
    fetchLocations,
    createLocation,
    updateLocation,
    deleteLocation,
  } = useLocationsStore();

  const canEditLocations = useHasPermission(Permission.LOCATIONS_EDIT);

  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [currentSearch, setCurrentSearch] = useState("");
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [editingLocationId, setEditingLocationId] = useState<string | null>(
    null,
  );
  const [locationToDelete, setLocationToDelete] = useState<Location | null>(
    null,
  );

  const {
    entitiesArray: locationsArray,
    handleSearch: baseHandleSearch,
    setDisplayedEntityIds,
  } = useEntitySearch({
    entities: locations,
    fetchEntities: async (params) => {
      const result = await fetchLocations({
        search: params?.search,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setTotal(result.total);
      return result.ids;
    },
  });

  const handleSearch = useCallback(
    (value: string) => {
      setPage(0);
      setCurrentSearch(value);
      baseHandleSearch(value);
    },
    [baseHandleSearch],
  );

  const refreshPage = useCallback(
    async (nextPage = page) => {
      const result = await fetchLocations({
        search: currentSearch || undefined,
        limit: PAGE_SIZE,
        offset: nextPage * PAGE_SIZE,
      });
      setTotal(result.total);
      setDisplayedEntityIds(result.ids);
    },
    [currentSearch, fetchLocations, page, setDisplayedEntityIds],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      refreshPage(newPage);
    },
    [refreshPage],
  );

  const handleConfirmDelete = async () => {
    if (!locationToDelete) {
      return;
    }
    const result = await deleteLocation(locationToDelete.id);
    if (!result.success) {
      toast.error(result.error ?? "Failed to delete location");
      return;
    }
    toast.success("Location deleted");
    setLocationToDelete(null);
    await refreshPage();
  };

  const columns: DataTableColumn<Location>[] = [
    {
      key: "name",
      header: "Name",
      type: "string",
    },
    {
      key: "city",
      header: "City",
      type: "string",
      format: (_value, location) => location.address?.city ?? "-",
    },
    {
      key: "state",
      header: "State",
      width: 100,
      format: (_value, location) => location.address?.state ?? "-",
    },
    {
      key: "description",
      header: "Description",
      type: "string",
      format: (_value, location) => location.description || "-",
    },
    {
      key: "syncStatus",
      header: "Sync",
      width: 90,
      type: "string",
      className: "hidden md:table-cell",
      format: (_value, location) =>
        location.externalMappings.length > 0 ? (
          <Badge variant="secondary">Synced</Badge>
        ) : (
          "-"
        ),
    },
    {
      key: "actions",
      header: "",
      width: 60,
      format: (_value, location) => {
        if (!canEditLocations) {
          return null;
        }

        const isDeleting =
          locationLoadingStates.get(location.id)?.isLoadingDelete ?? false;
        const isSynced = location.externalMappings.length > 0;
        return (
          <span
            title={isSynced ? "Managed by an integration" : undefined}
            className="inline-flex"
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                setLocationToDelete(location);
              }}
              disabled={isSynced || isDeleting}
            >
              {isDeleting ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </span>
        );
      },
    },
  ];

  if (listError) {
    return (
      <div className="p-8 text-destructive">
        Error loading locations: {listError}
      </div>
    );
  }

  const editingLocation = editingLocationId
    ? locations.get(editingLocationId)
    : undefined;

  return (
    <div className="flex h-full flex-col">
      <DataTable
        columns={columns}
        data={locationsArray}
        searchPlaceholder="Search locations..."
        className="h-full"
        isLoading={listLoading && locationsArray.length === 0}
        onCreate={
          canEditLocations ? () => setIsCreateSheetOpen(true) : undefined
        }
        createButtonText="New Location"
        onClickRow={(location) => setEditingLocationId(location.id)}
        onChangeSearch={handleSearch}
        pagination={{
          page,
          pageSize: PAGE_SIZE,
          total,
          onPageChange: handlePageChange,
        }}
      />

      <LocationEditorSheet
        open={isCreateSheetOpen}
        onOpenChange={setIsCreateSheetOpen}
        mode="create"
        onSave={async (data) => {
          const { location, error } = await createLocation(data);
          if (location) {
            setDisplayedEntityIds((prev) => [location.id, ...prev]);
            return true;
          }
          return error ?? "Failed to create location";
        }}
      />

      {editingLocation && (
        <LocationEditorSheet
          open={!!editingLocationId}
          onOpenChange={(open) => !open && setEditingLocationId(null)}
          mode="edit"
          location={editingLocation}
          readOnly={!canEditLocations}
          onSave={async (data) => {
            const { location, error } = await updateLocation(
              editingLocation.id,
              data,
            );
            return location ? true : (error ?? "Failed to update location");
          }}
        />
      )}

      <DeleteConfirmationDialog
        open={!!locationToDelete}
        onOpenChange={(open) => !open && setLocationToDelete(null)}
        onConfirm={handleConfirmDelete}
        entityType="Location"
        itemName={locationToDelete?.name}
      />
    </div>
  );
}
